import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearLogs, fetchLogsData, logEntityActivityComment } from '@actions/logs-actions';

import NoCommentsIcon from '@icons/no-comments-icon.svg';

import { MenuItem, Select } from '@mui';
import { makeStyles } from 'tss-react/mui';

import LogItem from '@shared/comments/log-item';
import CommentAvatarInput from '@shared/comments/comment-avatar-input';

import './comments.scss';

const SORT_ASC = 'entry__id';
const SORT_DESC = '-entry__id';

const useStyles = makeStyles()({
  select: {
    height: 'auto',
    maxWidth: '7.875rem',
    fontSize: '0.875rem',
    fontFamily: 'Roboto',
    // ellipsis text
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // selected value padding
    '& .MuiSelect-select': {
      paddingRight: '2.25rem'
    },
    // dropdown icon
    '& .MuiSelect-icon': {
      padding: 0,
      width: 'auto'
    }
  },
  menuPaper: {
    maxHeight: 400
  }
});

const EntityComments = () => {
  const { classes } = useStyles();
  const { data } = useSelector(state => state.dataDetail);
  const { list, next, reload, count } = useSelector(state => state.logs);
  const [sortOrder, setSortOrder] = useState(SORT_DESC);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearLogs());
    dispatch(fetchLogsData('map', 'entity', data?.id, null, { filters: 'comments_only_filters' }, sortOrder));
  }, [data?.id, sortOrder, dispatch]);

  useEffect(() => {
    if (reload) {
      dispatch(clearLogs());
      dispatch(fetchLogsData('map', 'entity', data?.id, null, { filters: 'comments_only_filters' }, sortOrder));
    }
  }, [reload, data?.id, sortOrder, dispatch]);

  const onScroll = useCallback(
    event => {
      const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
      if (bottom && next) {
        dispatch(fetchLogsData('map', 'entity', data?.id, next, { filters: 'comments_only_filters' }));
      }
    }, [data?.id, dispatch, next]);

  const onComment = useCallback((comment, mentions) => {
    const payload = {
      id: data?.id,
      comment,
      mentions
    };
    dispatch(logEntityActivityComment(payload));
  }, [data?.id, dispatch]);

  const onSortOrderChange = useCallback(
    event => {
      const value = event.target.value;
      if (sortOrder === value) {
        return;
      }
      setSortOrder(value);
    },
    [sortOrder]
  );

  const body = useMemo(() => {
    if (list?.length > 0) {
      return (
        <div styleName="comment-body">
          <CommentAvatarInput onComment={onComment} />
          {list.map((item, index) => <LogItem key={index} item={item} />)}
        </div>
      );
    }

    return (
      <div styleName="comment-body-empty-wrapper">
        <CommentAvatarInput onComment={onComment} />
        <div styleName="comment-body-empty">
          <img styleName= "comment-body-img" src={NoCommentsIcon} alt="NoCommentIcon"/>
          <div styleName="comment-body-empty-text">No comments yet</div>
          <div styleName="comment-body-empty-subtext">Leave a comment to get others updated with changes. Or discuss details.</div>
        </div>
      </div>
    );
  }, [list, onComment]);

  const headerCountText = useMemo(() => count ? count : 0, [count]);

  if (!data) {
    return null;
  }

  return (
    <div styleName="comment-container" onScroll={onScroll}>
      <div styleName="comment-content">
        <div styleName="comment-header">
          <div styleName="comment-header-text"> { headerCountText } Comments</div>
          <div styleName="comment-sort-order">
            <Select
              onChange={onSortOrderChange}
              value={sortOrder}
              className={classes.select}
              disableUnderline
              aria-label="Sort comments"
              MenuProps={{
                PaperProps: {
                  className: classes.menuPaper
                }
              }}
            >
              <MenuItem key={SORT_DESC} value={SORT_DESC}>Newest first</MenuItem>
              <MenuItem key={SORT_ASC} value={SORT_ASC}>Oldest first</MenuItem>
            </Select>
          </div>
        </div>
        {body}
      </div>
    </div>
  );
};

export default memo(EntityComments);
